/**
 * EntityCard - Card for displaying entity class with instances and AI suggestions button
 * Domain-colored with context-aware AI suggestions (reuses entity suggestions service)
 * Features flip animation to show educational content
 */

import { useState } from 'react'
import { Plus, Check, Sparkles, Users, Target, Lightbulb, TrendingUp, Loader2, Info, CheckCircle2, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { EntityDomain } from '@ispot/shared'
import type { EntityTreeInstance } from '@/lib/api/client'
import { getClassByUri } from '@/lib/ontology/schema'
import { expandPrefix } from '@/lib/ontology/namespaces'

export interface AISuggestion {
  id: string
  label: string
  reason?: string
  confidence?: number
}

interface EntityCardProps {
  classUri: string
  className: string
  domain: EntityDomain
  description?: string
  isRequired: boolean
  minCount: number
  currentCount: number
  instances: EntityTreeInstance[]
  onAddEntity?: (classUri: string) => void
  /** Callback when an instance tag is clicked */
  onInstanceClick?: (instance: EntityTreeInstance) => void
  /** Callback when AI suggestions button is clicked */
  onAISuggestionsClick?: (classUri: string, className: string, domain: EntityDomain) => void
  /** Currently loaded suggestions for this entity type */
  suggestions?: AISuggestion[]
  /** Loading state for suggestions */
  isLoadingSuggestions?: boolean
  /** Callback when a suggestion is accepted */
  onAcceptSuggestion?: (suggestion: AISuggestion, classUri: string) => void
}

// Domain colors - icon backgrounds only (no border)
const DOMAIN_STYLES: Record<EntityDomain, { bg: string; text: string; iconBg: string }> = {
  market: {
    bg: 'bg-market/10',
    text: 'text-market',
    iconBg: 'bg-market/15',
  },
  business: {
    bg: 'bg-business/10',
    text: 'text-business',
    iconBg: 'bg-business/15',
  },
  solution: {
    bg: 'bg-solution/10',
    text: 'text-solution',
    iconBg: 'bg-solution/15',
  },
}

// Icons by entity class name
const CLASS_ICONS: Record<string, React.ReactNode> = {
  Industry: <TrendingUp className="w-4 h-4" />,
  CustomerSegment: <Users className="w-4 h-4" />,
  Persona: <Users className="w-4 h-4" />,
  CustomerPain: <Target className="w-4 h-4" />,
  CustomerJob: <Target className="w-4 h-4" />,
  Competitor: <Target className="w-4 h-4" />,
  Trend: <TrendingUp className="w-4 h-4" />,
  ValueProposition: <Lightbulb className="w-4 h-4" />,
  Feature: <Lightbulb className="w-4 h-4" />,
}

export function EntityCard({
  classUri,
  className,
  domain,
  description,
  isRequired,
  minCount,
  currentCount,
  instances,
  onAddEntity,
  onInstanceClick,
  onAISuggestionsClick,
  suggestions,
  isLoadingSuggestions,
  onAcceptSuggestion,
}: EntityCardProps) {
  const [isFlipped, setIsFlipped] = useState(false)

  // Defensive: default to 'market' if domain is invalid
  const domainStyle = DOMAIN_STYLES[domain] || DOMAIN_STYLES.market
  // Show all non-rejected instances (pending + confirmed) to match the count display
  const activeInstances = instances.filter(i => i.status !== 'rejected')
  const isComplete = currentCount >= minCount && minCount > 0
  const hasSuggestions = suggestions && suggestions.length > 0

  // Get educational content from ontology schema
  // classUri may be prefixed (e.g., "market:Competitor") - expand to full URI
  const fullUri = classUri.includes(':') && !classUri.startsWith('http')
    ? expandPrefix(classUri)
    : classUri
  const ontologyClass = getClassByUri(fullUri)
  const educational = ontologyClass?.educational

  return (
    <div
      className="h-full"
      style={{ perspective: '1000px' }}
    >
      <div
        className="relative w-full h-full"
        style={{
          transformStyle: 'preserve-3d',
          transition: 'transform 0.5s ease-in-out',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front of Card */}
        <div
          className="absolute inset-0"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="h-full bg-white border border-border-subtle rounded-xl overflow-hidden transition-all hover:shadow-md flex flex-col relative">
            {/* Info button - top right corner */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                setIsFlipped(true)
              }}
              className="absolute top-3 right-3 w-6 h-6 rounded-full bg-warm-100 hover:bg-warm-200 flex items-center justify-center text-text-muted hover:text-text transition-colors z-10"
              title="Learn more about this entity"
            >
              <Info className="w-3.5 h-3.5" />
            </button>

            {/* Header - compact */}
            <div className="p-4 pb-2 pr-12">
              {/* Icon */}
              <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center mb-3', domainStyle.iconBg, domainStyle.text)}>
                {CLASS_ICONS[className] || <Target className="w-5 h-5" />}
              </div>

              {/* Title row */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                  <span className="font-display font-semibold text-text">{formatClassName(className)}</span>
                  {isRequired && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-market/15 text-market">
                      Required
                    </span>
                  )}
                </div>
                <div
                  className={cn(
                    'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
                    isComplete ? 'bg-market/10 text-market' : 'bg-warm-100 text-warm-600'
                  )}
                >
                  {isComplete && <Check className="w-3 h-3" />}
                  {minCount > 0 ? `${currentCount}/${minCount}` : currentCount}
                </div>
              </div>
              {description && (
                <p className="text-xs text-text-muted line-clamp-1 mt-1">{description}</p>
              )}
            </div>

            {/* Tags section - compact */}
            <div className="px-4 pb-3 flex-1">
              <div className="flex flex-wrap gap-1.5">
                {activeInstances.map((instance) => (
                  <button
                    key={instance.uri}
                    onClick={() => onInstanceClick?.(instance)}
                    className="px-2 py-0.5 rounded bg-warm-100 text-xs text-warm-700 hover:bg-warm-200 transition-colors cursor-pointer"
                    title={`${instance.status === 'pending' ? 'Pending' : 'Confirmed'} - Click to view`}
                  >
                    {instance.label}
                  </button>
                ))}
                <button
                  onClick={() => onAddEntity?.(classUri)}
                  className="flex items-center gap-1 px-2 py-0.5 border border-dashed border-border rounded text-xs text-text-muted hover:text-market hover:border-market transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  Add
                </button>
              </div>
            </div>

            {/* AI Suggestions - only show if there's content */}
            {(isLoadingSuggestions || hasSuggestions || onAISuggestionsClick) && (
              <div className="px-4 py-2 border-t border-border-subtle bg-warm-50/30">
                {isLoadingSuggestions ? (
                  <div className="flex items-center gap-1.5 text-xs text-text-muted">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Getting suggestions...
                  </div>
                ) : hasSuggestions ? (
                  <div className="flex flex-wrap gap-1 items-center">
                    <Sparkles className="w-3 h-3 text-market" />
                    {suggestions!.slice(0, 3).map((suggestion) => (
                      <button
                        key={suggestion.id}
                        onClick={() => onAcceptSuggestion?.(suggestion, classUri)}
                        className="flex items-center gap-0.5 px-1.5 py-0.5 bg-white border border-border-subtle rounded text-[11px] text-warm-600 hover:border-market hover:text-market transition-all"
                        title={suggestion.reason || 'Click to add'}
                      >
                        {suggestion.label}
                        <Plus className="w-2.5 h-2.5" />
                      </button>
                    ))}
                    {suggestions!.length > 3 && (
                      <span className="text-[11px] text-text-muted">+{suggestions!.length - 3}</span>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => onAISuggestionsClick?.(classUri, className, domain)}
                    className="flex items-center gap-1 text-xs text-market hover:underline"
                  >
                    <Sparkles className="w-3 h-3" />
                    Get AI Suggestions
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Back of Card (Educational Content) */}
        <div
          className="absolute inset-0"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div className="h-full bg-white border border-border-subtle rounded-xl overflow-hidden flex flex-col">
            {/* Back Header */}
            <div className="px-4 py-3 border-b border-border-subtle bg-warm-50/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', domainStyle.iconBg, domainStyle.text)}>
                  {CLASS_ICONS[className] || <Target className="w-4 h-4" />}
                </div>
                <span className="font-display font-semibold text-text text-sm">{formatClassName(className)}</span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsFlipped(false)
                }}
                className="p-1 rounded-md text-text-muted hover:text-text hover:bg-warm-100 transition-colors"
                title="Back to card"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>

            {/* Educational Content */}
            {educational ? (
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {/* What is this? */}
                <div>
                  <h4 className={cn('text-[10px] font-semibold uppercase tracking-wide mb-1', domainStyle.text)}>
                    What is this?
                  </h4>
                  <p className="text-xs text-text leading-relaxed">
                    {educational.whatIsThis}
                  </p>
                </div>

                {/* When to Use */}
                {educational.whenToUse && educational.whenToUse.length > 0 && (
                  <div>
                    <h4 className={cn('text-[10px] font-semibold uppercase tracking-wide mb-1', domainStyle.text)}>
                      When to use
                    </h4>
                    <ul className="space-y-0.5">
                      {educational.whenToUse.map((item, i) => (
                        <li key={i} className="text-xs text-text-secondary pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:w-1.5 before:h-1.5 before:rounded-full before:bg-warm-300">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Examples Grid */}
                <div className="grid grid-cols-2 gap-2">
                  {educational.goodExamples && educational.goodExamples.length > 0 && (
                    <div>
                      <h4 className="text-[10px] font-semibold text-market mb-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Good Examples
                      </h4>
                      <div className="space-y-1">
                        {educational.goodExamples.slice(0, 3).map((example, i) => (
                          <div key={i} className="text-[10px] text-text-secondary px-2 py-1 bg-market/5 rounded">
                            {example}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {educational.badExamples && educational.badExamples.length > 0 && (
                    <div>
                      <h4 className="text-[10px] font-semibold text-red-600 mb-1 flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        Bad Examples
                      </h4>
                      <div className="space-y-1">
                        {educational.badExamples.slice(0, 3).map((example, i) => (
                          <div key={i} className="text-[10px] text-text-secondary px-2 py-1 bg-red-50 rounded">
                            {example}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center px-4">
                <p className="text-xs text-text-muted text-center">
                  No educational content available.
                </p>
              </div>
            )}

            {/* Footer Tags */}
            <div className="px-4 py-2 border-t border-border-subtle bg-warm-50/50 flex items-center gap-2 flex-wrap">
              {educational?.discoveryPhase && (
                <span className="text-[10px] px-2 py-0.5 rounded bg-warm-100 text-text-secondary">
                  {educational.discoveryPhase}
                </span>
              )}
              <span className={cn('text-[10px] px-2 py-0.5 rounded', domainStyle.bg, domainStyle.text)}>
                {domain}
              </span>
              {isRequired && (
                <span className="text-[10px] px-2 py-0.5 rounded bg-market/15 text-market font-medium">
                  Required
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Format class name for display
function formatClassName(name: string): string {
  // Split camelCase/PascalCase
  return name.replace(/([a-z])([A-Z])/g, '$1 $2')
}
